import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const basicAsyncErrorHandlingCode = `// The two primary patterns for catching async failures

// 1. The Async/Await Try-Catch
async function fetchSafeData() {
    try {
        const response = await fetch('/api/data');
        if (!response.ok) throw new Error("HTTP Error: " + response.status);
        return await response.json();
    } catch (err) {
        // Catches both network errors AND manual throws
        console.error("Caught in try-catch:", err.message);
        return { fallback: true };
    }
}

// 2. The Promise Catch (Functional approach)
const loadData = () => 
    fetch('/api/data')
        .then(r => r.json())
        .catch(err => {
            console.error("Caught in .catch():", err.message);
            throw err; // Propagating if necessary
        });`;

const silentFailureCode = `// CRITICAL: The "Silent Failure" Trap

//  ANTI-PATTERN: Error inside a nested callback
async function riskyOperation() {
    try {
        setTimeout(() => {
            // This error is NOT caught by the parent try-catch!
            // It runs in a separate macrotask once 'riskyOperation' is dead.
            throw new Error("I am a silent killer");
        }, 100);
    } catch (err) {
        console.log("This will never run!");
    }
}

// SOLUTION: Promisify the timeout
const delay = (ms) => new Promise(res => setTimeout(res, ms));

async function safeOperation() {
    try {
        await delay(100);
        throw new Error("I am caught properly");
    } catch (err) {
        console.log("Caught:", err.message);
    }
}`;

const resiliencePatternsCode = `// Building resilient async systems

// 1. Retry with Exponential Backoff
async function fetchWithRetry(url, attempts = 3) {
    for (let i = 0; i < attempts; i++) {
        try {
            return await fetch(url).then(r => r.json());
        } catch (err) {
            if (i === attempts - 1) throw err;
            const delay = Math.pow(2, i) * 1000;
            await new Promise(res => setTimeout(res, delay));
        }
    }
}

// 2. Global Unhandled Rejection Safety Net
window.addEventListener('unhandledrejection', event => {
    // Crucial for catching forgotten .catch() calls in production
    console.error("CRITICAL: Unhandled Promise Rejection", event.reason);
    event.preventDefault(); // Prevents default browser logging
});`;

const customErrorHandlingCode = `// Domain-driven Error Objects

class APIError extends Error {
    constructor(message, status, type) {
        super(message);
        this.name = 'APIError';
        this.status = status;
        this.type = type; // e.g., 'AUTH', 'RATE_LIMIT'
    }
}

async function getUser(id) {
    const res = await fetch(\`/api/v1/user/\${id}\`);
    if (res.status === 429) {
        throw new APIError("Too many requests", 429, 'RATE_LIMIT');
    }
    // ...
}

// Intelligent handling based on error metadata
try {
    await getUser(123);
} catch (err) {
    if (err instanceof APIError && err.type === 'RATE_LIMIT') {
        showRateLimitModal();
    }
}`;

const JSAsyncErrors = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Asynchronous JavaScript</div>
                <h1>Asynchronous Error Handling & Resilience</h1>
                <p className="hero-lead">
                    Discover why traditional error handling fails in asynchronous systems. Master
                    the patterns required to catch silent failures, prevent crashes with global
                    safety nets, and build resilient recovery loops with exponential backoff.
                </p>
            </div>

            <div className="content-section">
                <h2>The Async Propagation Problem</h2>
                <p>
                    In traditional synchronous JavaScript, errors propagate naturally up the Call Stack. If
                    a function throws an error, the engine looks at the immediate caller, then the caller's
                    caller, until it finds a `try/catch` block or crashes the process. However,
                    asynchronous operations typically finish **after** their original call stack has already
                    unwound and been cleared. If a callback inside a `setTimeout` or a Promise resolution
                    encounters an error, there is no "parent" stack left to catch it. This fundamental
                    disconnect is what leads to "silent failures," where an application stops working despite
                    the console appearing clean. To build production-grade software, you must understand
                    how to bridge this temporal gap and ensure that every asynchronous path has a
                    defined failure strategy.
                </p>
                <p>
                    When using `async/await`, the language provides a bridge by making asynchronous errors
                    behave like synchronous ones within the function body. When you `await` a promise, the
                    engine effectively "re-throws" any rejection back into your local execution context.
                    This allows you to wrap multiple sequence steps in a single `try` block, maintaining
                    the same mental model you use for standard logic. However, it is vital to remember
                    that this re-throwing only happens for the **awaited** result. If you trigger an
                    unawaited background task or use an old-school callback inside your `async` function,
                    your local `try/catch` will be blind to its failures. This "blind spot" is one of the
                    most common sources of bugs in modern web development.
                </p>

                <CodeBlock code={basicAsyncErrorHandlingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Unhandled Rejection Safety Net</h2>
                <p>
                    Despite our best efforts, it is statistically likely that a complex application will
                    eventually forget to handle a Promise rejection. In the early days of Promises, these
                    errors would simply disappear into the void. Modern environments now provide the
                    `unhandledrejection` event on the global `window` object (or `process` in Node.js)
                    specifically to serve as a safety net. This event is triggered whenever a Promise
                    is rejected and no `.catch()` or `try/catch` has been attached to it within the same
                    turn of the event loop. Implementing a global logger for this event is an essential
                    best practice for production, as it allows you to capture and report failures to
                    monitoring services like Sentry or Datadog, even when they occur in unexpected corners
                    of your codebase.
                </p>
                <p>
                    Beyond just logging, the `unhandledrejection` handler can be used to prevent
                    applications from entering an inconsistent state. For example, if a critical
                    initialization Promise fails silently, your UI might show a "half-loaded" dashboard
                    that allows users to perform invalid actions. By catching these globally, you can
                    trigger a "Panic Mode" UI that suggests the user refreshes the page or contacts support.
                    It's important to note, however, that this should be your **last line of defense**, not
                    your primary error handling strategy. Relying solely on global handlers makes it
                    impossible to recover locally or provide granular feedback to the user. Every
                    intentional asynchronous action should ideally have its own dedicated failure
                    reception logic.
                </p>

                <CodeBlock code={silentFailureCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Resilience and Recovery Patterns</h2>
                <p>
                    Capturing an error is only half the battle; the other half is recovering from it. Many
                    asynchronous failures are "transient," meaning they are caused by temporary network
                    hiccups or server-side rate limits that will resolve themselves if given a few moments.
                    Implementing **Exponential Backoff** is the industry-standard way to handle these.
                    Instead of retrying immediately (which might exacerbate a server outage), you wait a
                    doubling amount of time between each attempt (1s, 2s, 4s, etc.). Adding a small amount
                    of random "Jitter" to these wait times is also recommended in high-traffic systems
                    to prevent thousands of clients from slamming the server at the exact same
                    millisecond. These resilience patterns transform a fragile application into a
                    robust system that can weather a stormy internet.
                </p>
                <p>
                    Another powerful pattern is the use of **Custom Error Objects** with metadata.
                    Standard JavaScript `Error` objects only provide a message string, which is often
                    insufficient for programmatic recovery. By extending the `Error` class, you can
                    attach additional context like HTTP status codes, machine-readable error codes (e.g.,
                    `ERR_USER_NOT_FOUND`), and whether or not the error should be considered "retryable".
                    This allows your UI layer to differentiate between a "Permanent Forbidden" (don't
                    retry, show login) and a "Temporary Timeout" (auto-retry). This domain-driven
                    approach to errors ensures that your business logic remains decoupled from the
                    low-level implementation details of your network requests or file system operations.
                </p>

                <CodeBlock code={resiliencePatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    Never leave a Promise unhandled; even if you don't expect it to fail, add a `.catch(err {"=>"} {"{ }"})`
                    to explicitly signal your intent. In `async` functions, consistently use `try/catch` around
                    blocks of code that interact with external services. When propagating errors up a chain,
                    it is often helpful to "wrap" the error in a new `Error` object that adds more context
                    about the current operation while preserving the original stack trace. Avoid
                    "Error Swallowing," the practice of catching an error and doing nothing with it, as it
                    makes debugging production issues nearly impossible. Finally, ensure that your
                    automated tests specifically cover "Unsatisfied Paths"the scenarios where the network
                    fails or the disk is fullnot just the "Happy Path" where everything works perfectly.
                </p>

                <CodeBlock code={customErrorHandlingCode} language="javascript" />

                <KeyFacts><ul>
                        <li>**Propagation:** Always bridge the temporal gap using `await` or `.catch()`.</li>
                        <li>**Transparency:** Avoid silent failures by catching errors in the correct macrotask.</li>
                        <li>**Safety:** Implement `unhandledrejection` as a global production safety net.</li>
                        <li>**Resilience:** Use Exponential Backoff for transient network issues.</li>
                        <li>**Metadata:** Extend the `Error` class to include status codes and error types.</li>
                        <li>**Continuity:** Use `.finally()` to reset loading states and clean up resources.</li>
                        <li>**Validation:** Test your application's failure states as rigorously as its success states.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSAsyncErrors;


